/**
 * Navigation helpers. Admin sidebar uses BRD layout; other roles use login pageData.
 */

import { ADMIN_ROLE_TYPE, TEACHER_ROLE_TYPE } from './constant';

const HIDDEN_SIDEBAR_MODULES = ['Schedules', 'Analytics', 'Give Feedback'];

const CLASSROOM_OBSERVATIONS_MODULE = 'Classroom Observations';
const PRODUCT_ANALYTICS_MODULE = 'Product Analytics';
const TEACHER_OBSERVATION_ANALYTICS_PAGE = 'Teacher Observation Analytics';

const MERGED_INTO_ADMINISTRATION = ['User Management', 'Location Management'];

const ADMIN_SIDEBAR_MENU_ORDER = [
  'Content Management',
  'Resources',
  'Teacher Evaluation',
  PRODUCT_ANALYTICS_MODULE,
  'Administration'
];
/** Modules excluded from role creation UI and payloads */
export const HIDDEN_ROLE_MODULES = [
  'Schedules',
  'Session Schedules',
  'Analytics',
  'Product Analytics'
];

/** Role levels shown in role management dropdowns */
export const ALLOWED_ROLE_GROUP_LABELS = [
  'PAF Central Office',
  'District',
  'Area',
  'School',
  'Class'
];

/** Parent roles from /metadata/roleGroups */
export const ALLOWED_PARENT_ROLE_LABELS = [
  'Admin',
  'Leadership',
  'Employees',
  'Curriculum Managers',
  'Program Heads',
  'District Manager',
  'District Education Office',
  'Area Manager',
  'Program Managers',
  'Facilitators',
  'Supervisor',
  'Teacher (Self)',
  'Student (Self)',
  'Teacher',
  'Facilitator',
  'Student'
];

/** Content type options shown in content creation UI */
export const ALLOWED_CONTENT_TYPE_OPTIONS = [
  { label: 'State', id: 'State' },
  { label: 'Core', id: 'Core' }
];

/** Role access: content type permissions (API payload keys) */
export const ROLE_CONTENT_TYPE_ACCESS_OPTIONS = [
  { label: 'Core', key: 'coreContent' },
  { label: 'State', key: 'stateContent' }
];

export const isHiddenRoleModule = moduleName =>
  HIDDEN_ROLE_MODULES.includes(moduleName);

export const isAllowedParentRoleLabel = label =>
  ALLOWED_PARENT_ROLE_LABELS.some(
    allowed => allowed.toLowerCase() === String(label || '').toLowerCase()
  );

export const isAllowedUserRole = role =>
  isAllowedParentRoleLabel(role?.parentRole) ||
  isAllowedParentRoleLabel(role?.roleName);

export const mapContentTypeToApi = label => {
  const normalized = String(label ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'core' || normalized === 'code') {
    return 'CORE';
  }
  if (normalized === 'state') {
    return 'STATE';
  }
  return label;
};

export const mapContentTypeToFormOption = apiType => {
  const normalized = String(apiType ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'core' || normalized === 'code') {
    return { label: 'Core', id: 'Core' };
  }
  if (normalized === 'state') {
    return { label: 'State', id: 'State' };
  }
  return apiType ? { label: apiType, id: apiType } : null;
};

export const isCoreContentType = type => {
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase();
  return normalized === 'core' || normalized === 'code';
};

export const formatContentTypeLabel = type => {
  if (isCoreContentType(type)) {
    return 'Core';
  }
  const normalized = String(type ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'state') {
    return 'State';
  }
  return type || '';
};

export const normalizeContentPublishStatus = status => {
  const normalized = String(status ?? '')
    .trim()
    .toLowerCase();
  if (
    !normalized ||
    normalized === 'saved' ||
    normalized === 'save' ||
    normalized === 'draft'
  ) {
    return 'Saved';
  }
  if (normalized === 'publish' || normalized === 'published') {
    return 'Publish';
  }
  return String(status ?? '').trim();
};

export const resolveContentRowId = row =>
  row?.contentId ?? row?.id ?? row?.contentID ?? null;

export const DASHBOARD_MODULE_NAME = 'Dashboard';

export const isTeacherRoleType = roleType =>
  TEACHER_ROLE_TYPE.includes(roleType);

export const isAdminRoleType = roleType => roleType === ADMIN_ROLE_TYPE;

/** Non-admin roles use login pageData as-is (enabled pages only). */
export const isRoleBasedNavigationRole = roleType =>
  Boolean(roleType) && !isAdminRoleType(roleType);

export const isDashboardModuleName = name =>
  String(name ?? '')
    .trim()
    .toLowerCase() === 'dashboard';

export const isDashboardPageName = name =>
  String(name ?? '')
    .trim()
    .toLowerCase() === 'dashboard';

export const getDashboardPagesFromMetadata = (allPages, moduleId) => {
  const page = findDashboardPageInMetadata(allPages, moduleId);
  return page ? [page] : [];
};

export const findDashboardPageInMetadata = (allPages, moduleId) => {
  if (!Array.isArray(allPages)) {
    return null;
  }

  const byModule = allPages.find(page => page?.moduleId === moduleId);
  if (byModule) {
    return byModule;
  }

  return (
    allPages.find(
      page =>
        isDashboardPageName(page?.pageName) ||
        isDashboardPageName(page?.moduleName) ||
        isDashboardPageName(page?.pageRoute) ||
        String(page?.pageUrl ?? '')
          .toLowerCase()
          .includes('dashboard')
    ) || null
  );
};

export const resolveDashboardPageIds = (allPages, moduleId) => {
  const dashboardPage = findDashboardPageInMetadata(allPages, moduleId);
  if (dashboardPage?.pageId != null && dashboardPage.pageId !== '') {
    return [dashboardPage.pageId];
  }

  if (moduleId != null && moduleId !== '') {
    return [moduleId];
  }

  return [];
};

export const buildDashboardPageEntry = (
  allPages,
  moduleId,
  isChecked = false
) => {
  const matchedPage = findDashboardPageInMetadata(allPages, moduleId);
  const [resolvedPageId] = resolveDashboardPageIds(allPages, moduleId);

  return {
    pageId: resolvedPageId ?? moduleId,
    pageName: matchedPage?.pageName || 'dashboard',
    moduleId,
    isChecked
  };
};

export const hasDashboardMenuAccess = menuNames =>
  Array.isArray(menuNames) &&
  menuNames.some(
    name => isDashboardPageName(name) || isDashboardModuleName(name)
  );

export const hasDashboardPageDataAccess = pageData => {
  if (!pageData || typeof pageData !== 'object') {
    return false;
  }

  if (!Object.prototype.hasOwnProperty.call(pageData, DASHBOARD_MODULE_NAME)) {
    return false;
  }

  const pages = pageData[DASHBOARD_MODULE_NAME];
  if (!Array.isArray(pages) || pages.length === 0) {
    return true;
  }

  return pages.some(page => page?.status);
};

export const ADMIN_USERS_PAGE_NAME = 'Users';
export const ADMIN_SCHOOL_PAGE_NAME = 'School';

/** Sidebar menu already built (e.g. restored from localStorage). */
export const isTransformedSidebarMenu = pageData =>
  Boolean(pageData?.Administration) &&
  !pageData['User Management'] &&
  !pageData['Location Management'];

const hasModuleAccess = pages =>
  Array.isArray(pages) && pages.some(page => page?.status);

const buildAdminSubPage = (pageName, sourcePage) => ({
  ...(sourcePage || {}),
  pageName,
  status: true
});

const getTeacherObservationAnalyticsPages = pageData => {
  const pages = pageData?.[PRODUCT_ANALYTICS_MODULE] || [];
  return pages.filter(
    page =>
      page?.status && page?.pageName === TEACHER_OBSERVATION_ANALYTICS_PAGE
  );
};

/** Teacher / Student / Facilitator: show enabled pages from login pageData. */
export const transformRoleBasedSidebarMenu = pageData => {
  if (!pageData || typeof pageData !== 'object') {
    return {};
  }

  const filtered = {};

  Object.keys(pageData).forEach(moduleKey => {
    if (isDashboardModuleName(moduleKey)) {
      return;
    }

    const pages = pageData[moduleKey];
    if (!Array.isArray(pages)) {
      return;
    }

    if (pages.length === 0) {
      filtered[moduleKey] = [];
      return;
    }

    const enabledPages = pages.filter(page => page?.status);
    if (enabledPages.length > 0) {
      filtered[moduleKey] = enabledPages;
    }
  });

  return filtered;
};

const transformAdminSidebarMenu = pageData => {
  if (isTransformedSidebarMenu(pageData)) {
    const ordered = {};
    ADMIN_SIDEBAR_MENU_ORDER.forEach(moduleKey => {
      if (pageData[moduleKey]) {
        ordered[moduleKey] = pageData[moduleKey];
      }
    });
    return ordered;
  }

  const administrationPages = [];

  if (hasModuleAccess(pageData['User Management'])) {
    const sourcePage = pageData['User Management'].find(page => page?.status);
    administrationPages.push(
      buildAdminSubPage(ADMIN_USERS_PAGE_NAME, sourcePage)
    );
  }

  if (hasModuleAccess(pageData['Location Management'])) {
    const sourcePage =
      pageData['Location Management'].find(page => page?.status) ||
      pageData['Location Management'][0];
    administrationPages.push(
      buildAdminSubPage(ADMIN_SCHOOL_PAGE_NAME, sourcePage)
    );
  }

  const transformed = {};

  ADMIN_SIDEBAR_MENU_ORDER.forEach(moduleKey => {
    if (moduleKey === 'Administration') {
      if (administrationPages.length > 0) {
        transformed.Administration = administrationPages;
      }
      return;
    }

    if (moduleKey === PRODUCT_ANALYTICS_MODULE) {
      const analyticsPages = getTeacherObservationAnalyticsPages(pageData);
      if (analyticsPages.length > 0) {
        transformed[moduleKey] = analyticsPages;
      }
      return;
    }

    if (
      MERGED_INTO_ADMINISTRATION.includes(moduleKey) ||
      HIDDEN_SIDEBAR_MODULES.includes(moduleKey)
    ) {
      return;
    }

    const pages = pageData[moduleKey];
    if (!Array.isArray(pages)) {
      return;
    }

    if (pages.length === 0) {
      transformed[moduleKey] = [];
      return;
    }

    const enabledPages = pages.filter(page => page?.status);
    if (enabledPages.length > 0) {
      transformed[moduleKey] = enabledPages;
    }
  });

  return transformed;
};

export const transformSidebarMenu = (pageData, roleType) => {
  if (!pageData || typeof pageData !== 'object') {
    return {};
  }

  if (isAdminRoleType(roleType)) {
    return transformAdminSidebarMenu(pageData);
  }

  return transformRoleBasedSidebarMenu(pageData);
};

/** Route access from full login pageData (sidebar may show fewer items for admin). */
export const buildMenuArrayFromPageData = (pageData, roleType) => {
  if (!pageData || typeof pageData !== 'object') {
    return isAdminRoleType(roleType) ? ['dashboard'] : [];
  }

  const pageNames = [];

  Object.keys(pageData).forEach(category => {
    const pages = pageData[category];

    if (Array.isArray(pages) && pages.length > 0) {
      pages.forEach(page => {
        if (page?.status && page?.pageName) {
          pageNames.push(page.pageName);
        }
      });
      return;
    }

    if (
      isRoleBasedNavigationRole(roleType) &&
      Array.isArray(pages) &&
      pages.length === 0
    ) {
      pageNames.push(category);
    } else if (isAdminRoleType(roleType) && category) {
      pageNames.push(category);
    }
  });

  if (
    (isAdminRoleType(roleType) || hasDashboardPageDataAccess(pageData)) &&
    !pageNames.some(name => isDashboardPageName(name))
  ) {
    pageNames.push('dashboard');
  }

  const hadUserManagement = pageData['User Management']?.some(
    page => page?.status
  );
  const hadLocationManagement = pageData['Location Management']?.some(
    page => page?.status
  );

  if (hadUserManagement) {
    ['Users', 'User Groups', 'Roles and App Access'].forEach(name => {
      if (!pageNames.includes(name)) {
        pageNames.push(name);
      }
    });
  }

  if (hadLocationManagement) {
    [
      'States',
      'Districts',
      'Area',
      'Schools',
      ADMIN_SCHOOL_PAGE_NAME,
      'Add School'
    ].forEach(name => {
      if (!pageNames.includes(name)) {
        pageNames.push(name);
      }
    });
  }

  if (
    isAdminRoleType(roleType) &&
    hasModuleAccess(pageData[PRODUCT_ANALYTICS_MODULE]) &&
    !pageNames.includes(TEACHER_OBSERVATION_ANALYTICS_PAGE)
  ) {
    pageNames.push(TEACHER_OBSERVATION_ANALYTICS_PAGE);
  }

  return pageNames;
};
